import { Injectable } from '@angular/core';

import { ToastController } from 'src/app/components/ionic-replacements/ToastController';

/**
 * Affiliate parameters carried on an order, used to build the pixel-variable
 * script and to supply the raw `pixelsText` markup. Every field is optional
 * because `appendPixel` only runs when `pixelsText` is present and the original
 * component code read each id defensively (with `|| ''` fallbacks for subIds).
 */
export interface PixelAffParameters {
  pixelsText?: string;
  affId?: string;
  offId?: string;
  subId1?: string;
  subId2?: string;
  subId3?: string;
  subId4?: string;
  subId5?: string;
}

/** The order shape `appendPixel` reads from `orderDetailsInfo.navParams.order`. */
export interface PixelOrder {
  total?: number;
  affParameters?: PixelAffParameters;
}

/** The slice of `orderDetailsInfo` that `appendPixel` depends on. */
export interface PixelOrderDetailsInfo {
  navParams: {
    order?: PixelOrder;
  };
}

@Injectable({ providedIn: 'root' })
export class PixelInjectionService {
  appendPixel(orderDetailsInfo: PixelOrderDetailsInfo): void {
    // Pixel
    if (orderDetailsInfo.navParams.order &&
      orderDetailsInfo.navParams.order.affParameters &&
      orderDetailsInfo.navParams.order.affParameters.pixelsText) {
      const scriptElements = [];
      const pixelVariables = document.createElement('script');
      pixelVariables.innerHTML = `
        var pixelOrderTotal = ${orderDetailsInfo.navParams.order.total};
        var pixelaffId = '${orderDetailsInfo.navParams.order.affParameters.affId}';
        var pixeloffId = '${orderDetailsInfo.navParams.order.affParameters.offId}';
        var pixelSubId1 = '${orderDetailsInfo.navParams.order.affParameters.subId1 || ''}';
        var pixelSubId2 = '${orderDetailsInfo.navParams.order.affParameters.subId2 || ''}';
        var pixelSubId3 = '${orderDetailsInfo.navParams.order.affParameters.subId3 || ''}';
        var pixelSubId4 = '${orderDetailsInfo.navParams.order.affParameters.subId4 || ''}';
        var pixelSubId5 = '${orderDetailsInfo.navParams.order.affParameters.subId5 || ''}';
      `;
      scriptElements.push(pixelVariables);

      const div = document.createElement('div');
      div.innerHTML = orderDetailsInfo.navParams.order.affParameters.pixelsText;

      const scriptNodes = div.querySelectorAll('script');
      for (let i = 0; i < scriptNodes.length; i++) {
        const scriptNode: HTMLScriptElement = scriptNodes[i];
        const scriptElement = document.createElement('script');
        for (let aI = 0; aI < scriptNode.attributes.length; aI++) {
          scriptElement.attributes.setNamedItem(<Attr>scriptNode.attributes[aI].cloneNode());
        }
        const scriptContent = document.createTextNode(String(scriptNode.textContent));
        scriptElement.appendChild(scriptContent);
        scriptNode.remove();
        scriptElements.push(scriptElement);
      }
      document.body.appendChild(div);
      for (let i = 0; i < scriptElements.length; i++) {
        document.body.appendChild(scriptElements[i]);
      }
    }
  }

  async insertScripts(
    toastCtrl: ToastController,
    scriptsToLoad: Array<string>,
    className: string = ''
  ): Promise<void> {
    let scripts = document.getElementsByClassName(className);
    if (scripts.length === 0) {
      for (let i = 0; i < scriptsToLoad.length; i++) {
        await this.loadScript(scriptsToLoad[i], className).catch(e => {
          return toastCtrl
            .create({
              message: e.message,
              position: "top",
              showCloseButton: true,
              cssClass: "errorToast"
            })
            //.present();
        });
      }
    }
  }

  loadScript(scriptCode: string, className: string = ''): Promise<HTMLScriptElement> {
    return new Promise((resolve, reject) => {
      let script = document.createElement("script");
      script.src = scriptCode;
      if (className) {
        script.setAttribute("class", className);
      }

      document.head.appendChild(script);

      script.addEventListener('load', () => {
        resolve(script);
      });

      script.addEventListener('error', () => {
        reject(new Error(`${scriptCode} failed to load.`));
      });
    });
  }
}
